import { useEffect, useRef, useState } from "hono/jsx";

interface ImageHistoryItem {
  url: string;
  time: number;
}

interface ProfileAvatarUploaderProps {
  initialImage?: string | null;
  initialName?: string;
  initialColor?: string;
  initialFrame?: "circle" | "square";
  email?: string;
  maxProfileImages: number;
}

export const ProfileAvatarUploader = (
  {
    initialImage,
    initialName,
    initialColor,
    initialFrame,
    email,
    maxProfileImages,
  }: ProfileAvatarUploaderProps,
) => {
  const [image, setImage] = useState(initialImage);
  const [color, setColor] = useState(initialColor || "#570df8"); // Default primary
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceTimer = useRef<number | undefined>(undefined);

  // Gallery State
  const [history, setHistory] = useState<ImageHistoryItem[]>([]);
  const [showGallery, setShowGallery] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);

  // New UI States
  const [frameShape, setFrameShape] = useState<"circle" | "square">(
    initialFrame || "circle",
  );
  const [showReplacementModal, setShowReplacementModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchHistory = async () => {
    try {
      const res = await fetch("/profile/images");
      if (res.ok) {
        const data = await res.json();
        setHistory(data.images || []);
      }
    } catch (e) {
      console.error("Failed to fetch history", e);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const handleUpdate = async (formData: FormData) => {
    setUploading(true);
    setError(null);

    try {
      const res = await fetch("/profile/upload-avatar", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Update failed");
      }

      const data = await res.json();
      if (data.url !== undefined) setImage(data.url);
      if (data.color) setColor(data.color);

      // Refresh history after upload
      fetchHistory();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Update failed");
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = async (e: Event) => {
    const input = e.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];

    // Client-side validation
    if (file.size > 5 * 1024 * 1024) { // Increased client-side check to 5MB just in case, server enforces 300KB? Wait, server enforces 300KB.
      if (file.size > 300 * 1024) {
        setError("File too large (max 300KB)");
        return;
      }
    }
    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      setError("Invalid file type (jpg, png, webp only)");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    await handleUpdate(formData);

    // Reset input
    input.value = "";
  };

  const handleColorChange = (e: Event) => {
    const input = e.target as HTMLInputElement;
    const newColor = input.value;

    // 1. Update visual state immediately
    setColor(newColor);

    // 2. Clear existing timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // 3. Set new debounced save
    debounceTimer.current = setTimeout(async () => {
      const formData = new FormData();
      formData.append("color", newColor);
      await handleUpdate(formData);
    }, 500); // 500ms delay
  };

  const handleClear = () => {
    setShowRemoveModal(true);
  };

  const confirmClear = async () => {
    const formData = new FormData();
    formData.append("clearImage", "true");
    await handleUpdate(formData);
    setShowRemoveModal(false);
  };

  const handleFrameChange = async (newFrame: "circle" | "square") => {
    setFrameShape(newFrame);
    const formData = new FormData();
    formData.append("frame", newFrame);
    await handleUpdate(formData);
  };

  const handleSelectHistory = async (url: string) => {
    const formData = new FormData();
    formData.append("file", url); // Send URL as 'file' param, backend handles string check
    await handleUpdate(formData);
    setShowGallery(false);
  };

  const handleDeleteHistory = (e: Event, filename: string) => {
    e.stopPropagation();
    setImageToDelete(filename);
  };

  const confirmDelete = async () => {
    if (!imageToDelete) return;

    try {
      const formData = new FormData();
      formData.append("filename", imageToDelete);
      const res = await fetch("/profile/delete-image", {
        method: "POST",
        body: formData,
      });
      if (res.ok) {
        fetchHistory();
      }
    } catch (e) {
      console.error("Delete failed", e);
    } finally {
      setImageToDelete(null);
    }
  };

  return (
    <div class="flex flex-col gap-6 w-full">
      {/* Top Section: Avatar & Info */}
      <div class="flex items-start gap-6 px-4">
        {/* Avatar Column */}
        <div class="flex flex-col items-center gap-4">
          <div class="relative group">
            <div
              class={`avatar placeholder ${
                frameShape === "circle" ? "" : "rounded-none"
              }`}
            >
              <div
                class={`${
                  frameShape === "circle" ? "rounded-full" : "rounded-btn"
                } w-24 border-4 flex items-center justify-center overflow-hidden transition-all duration-300`}
                style={{
                  backgroundColor: color,
                  borderColor: color,
                  color: "#fff",
                }}
              >
                {image
                  ? (
                    <img
                      src={image}
                      alt="Profile"
                      class="w-full h-full object-cover"
                    />
                  )
                  : (
                    <span class="text-4xl font-bold">
                      {initialName?.charAt(0).toUpperCase() || "U"}
                    </span>
                  )}

                {/* Overlay for Image Upload */}
                <div
                  class={`absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity ${
                    frameShape === "square" ? "rounded-none" : "rounded-full"
                  } pointer-events-none`}
                >
                  <span class="text-white text-xs font-bold uppercase tracking-wider">
                    Change
                  </span>
                </div>
              </div>
            </div>

            {/* Hidden File Input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              class="hidden"
              onChange={handleFileChange}
              disabled={uploading}
              title="Upload image"
            />

            {/* Click Handler Overlay */}
            <div
              class="absolute inset-0 w-full h-full cursor-pointer z-10"
              onClick={() => {
                if (history.length >= maxProfileImages) {
                  setShowReplacementModal(true);
                } else {
                  fileInputRef.current?.click();
                }
              }}
            />

            {uploading && (
              <div class="absolute inset-0 flex items-center justify-center bg-black/30 rounded-full z-20">
                <span class="loading loading-spinner text-white"></span>
              </div>
            )}
          </div>

          <div class="flex items-center gap-2">
            {/* Shape Toggles */}
            <div class="join">
              <button
                type="button"
                class={`btn btn-xs join-item ${
                  frameShape === "circle" ? "btn-active" : ""
                }`}
                onClick={() => handleFrameChange("circle")}
                title="Circle Frame"
              >
                <div class="w-3 h-3 rounded-full border border-current" />
              </button>
              <button
                type="button"
                class={`btn btn-xs join-item ${
                  frameShape === "square" ? "btn-active" : ""
                }`}
                onClick={() => handleFrameChange("square")}
                title="Square Frame"
              >
                <div class="w-3 h-3 rounded-none border border-current" />
              </button>
            </div>

            {/* Color Picker */}
            <div class="tooltip" data-tip="Background Color">
              <input
                type="color"
                value={color}
                onInput={handleColorChange}
                class="w-8 h-8 rounded-full cursor-pointer border border-base-300 overflow-hidden p-0"
              />
            </div>

            {/* Clear Button (only if image exists) */}
            {image && (
              <button
                type="button"
                onClick={handleClear}
                class="btn btn-circle btn-ghost btn-sm text-error tooltip"
                data-tip="Remove Picture"
                disabled={uploading}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Info Column */}
        <div class="flex flex-col mt-6">
          <span class="text-2xl font-black uppercase tracking-tight">
            {initialName}
          </span>
          <span class="text-sm font-medium opacity-60">{email}</span>
        </div>
      </div>

      {error && (
        <div class="text-error text-xs font-bold uppercase tracking-wide">
          {error}
        </div>
      )}

      {/* Gallery Toggle */}
      {history.length > 0 && (
        <div class="w-full border-t border-base-300 pt-4">
          <button
            type="button"
            onClick={() => setShowGallery(!showGallery)}
            class="btn btn-ghost btn-sm w-full font-bold uppercase text-xs opacity-60 flex gap-2"
          >
            {showGallery
              ? `Hide Previous Pictures (${history.length}/${maxProfileImages})`
              : `View Previous Pictures (${history.length}/${maxProfileImages})`}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class={`w-4 h-4 transition-transform ${
                showGallery ? "rotate-180" : ""
              }`}
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="m19.5 8.25-7.5 7.5-7.5-7.5"
              />
            </svg>
          </button>

          {showGallery && (
            <div class="grid grid-cols-4 gap-2 mt-4 animate-in fade-in slide-in-from-top-2 duration-200">
              {history.map((item) => (
                <div class="relative group">
                  {/* Image Container */}
                  <div
                    class={`aspect-square overflow-hidden cursor-pointer border-2 ${
                      frameShape === "square" ? "rounded-none" : "rounded-full"
                    }`}
                    style={{
                      borderColor: color,
                      backgroundColor: color,
                    }}
                    onClick={() => handleSelectHistory(item.url)}
                  >
                    <img src={item.url} class="w-full h-full object-cover" />
                    {/* Hover Overlay */}
                    <div
                      class={`absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center ${
                        frameShape === "square"
                          ? "rounded-none"
                          : "rounded-full"
                      }`}
                    >
                      <span class="text-white text-[10px] font-bold uppercase">
                        Use
                      </span>
                    </div>
                    {/* Current Indicator */}
                    {image === item.url && (
                      <div class="absolute bottom-0 inset-x-0 bg-primary/80 text-primary-content text-[10px] font-bold uppercase text-center py-1">
                        Current
                      </div>
                    )}
                  </div>
                  {/* Delete Button - Outside overflow container */}
                  <button
                    type="button"
                    onClick={(e) =>
                      handleDeleteHistory(e, item.url.split("/").pop()!)}
                    class="absolute -top-1 -right-1 p-1 bg-error text-white rounded-full opacity-0 group-hover:opacity-100 hover:scale-110 transition-all z-10"
                    title="Delete"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      class="w-3 h-3"
                    >
                      <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {imageToDelete && (
        <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div class="card w-full max-w-sm bg-base-100 shadow-2xl animate-in zoom-in-95 duration-200">
            <div class="card-body p-6 text-center">
              <h3 class="font-black uppercase text-xl mb-2">Delete Image?</h3>
              <p class="text-sm opacity-70 mb-6">
                Are you sure you want to remove this image from your history?
                This action cannot be undone.
              </p>
              <div class="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  class="btn btn-ghost font-bold uppercase"
                  onClick={() => setImageToDelete(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="btn btn-error font-bold uppercase"
                  onClick={confirmDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Replacement Warning Modal */}
      {showReplacementModal && (
        <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div class="card w-full max-w-sm bg-base-100 shadow-2xl animate-in zoom-in-95 duration-200">
            <div class="card-body p-6 text-center">
              <h3 class="font-black uppercase text-xl mb-2">Gallery Full</h3>
              <p class="text-sm opacity-70 mb-4">
                You have reached the limit of {maxProfileImages}{" "}
                images. Proceeding will replace your oldest image:
              </p>

              {/* Oldest Image Preview */}
              {history.length > 0 && (
                <div class="flex justify-center mb-6">
                  <div class="avatar">
                    <div class="w-16 rounded-lg ring ring-error ring-offset-base-100 ring-offset-2 opacity-50">
                      <img src={history[history.length - 1].url} />
                    </div>
                  </div>
                </div>
              )}

              <div class="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  class="btn btn-ghost font-bold uppercase"
                  onClick={() => setShowReplacementModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="btn btn-primary font-bold uppercase"
                  onClick={() => {
                    setShowReplacementModal(false);
                    fileInputRef.current?.click();
                  }}
                >
                  Proceed
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove Confirmation Modal */}
      {showRemoveModal && (
        <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div class="card w-full max-w-sm bg-base-100 shadow-2xl animate-in zoom-in-95 duration-200">
            <div class="card-body p-6 text-center">
              <h3 class="font-black uppercase text-xl mb-2">Remove Picture?</h3>
              <p class="text-sm opacity-70 mb-6">
                Are you sure you want to remove your current profile picture?
              </p>
              <div class="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  class="btn btn-ghost font-bold uppercase"
                  onClick={() => setShowRemoveModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="btn btn-error font-bold uppercase"
                  onClick={confirmClear}
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
